import { useLazyQuery } from '@apollo/client';
import React, { memo, useEffect, useState } from 'react';
import shortid from 'shortid';
import CheckSvg from 'images/v5/icons/check.svg';
import Button from 'layout/atoms/Button/Button';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import Input from 'layout/atoms/Input/Input';
import parseImage from 'parsers/common/parseImage';
import styles from './Autocomplete.module.scss';
function Autocomplete({ className = '', query, name, mapEdges, mapNode, value, selectedValues = [], multiple = false, showOptionIcon = false, disabled = false, error = false, variables, variant, size, testId, onBlur, onChange, }) {
    const [focused, setFocused] = useState(false);
    const [options, setOptions] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [filterValue, setFilterValue] = useState('');
    // const [valueSelected, setValueSelected] = useState(false);
    const [getData, { data }] = useLazyQuery(query, { variables });
    useEffect(() => {
        getData();
    }, [getData]);
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);
    useEffect(() => {
        if (data) {
            const edges = mapEdges(data);
            const mappedOptions = edges.map((edge) => mapNode(edge.node));
            setOptions(mappedOptions);
            setFilteredOptions(mappedOptions);
        }
    }, [data, mapEdges, mapNode]);
    useEffect(() => {
        setFilteredOptions(options === null || options === void 0 ? void 0 : options.filter((option) => { var _a; return (_a = option.text) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(filterValue === null || filterValue === void 0 ? void 0 : filterValue.toLowerCase()); }));
    }, [filterValue, options]);
    const filterOptions = (options, value) => {
        setInputValue(value);
        setFilterValue(value);
    };
    const getOptionId = (option) => { var _a; return ((_a = option.value) === null || _a === void 0 ? void 0 : _a.id) || option.value; };
    const isOptionSelected = (option) => selectedValues.includes(getOptionId(option));
    const hasOptionIcon = (option) => showOptionIcon && Object.prototype.hasOwnProperty.call(option, 'thumbnail');
    const getOptionIcon = (option) => {
        if (!hasOptionIcon(option))
            return undefined;
        return (React.createElement("span", { className: styles.optionIcon }, option.thumbnail ? (React.createElement("img", { src: parseImage(option.thumbnail) || '', alt: "" })) : (React.createElement("span", { className: styles.emptyOptionIcon }))));
    };
    const getOptionLeading = (option) => {
        const optionIcon = getOptionIcon(option);
        const selectedIcon = multiple && isOptionSelected(option);
        const selectionMarker = selectedIcon ? React.createElement(CheckSvg, null) : undefined;
        const selectionSpacer = !selectedIcon && optionIcon ? React.createElement("span", { className: styles.checkSpacer }) : undefined;
        if (!optionIcon && !selectedIcon)
            return undefined;
        return (React.createElement("span", { className: styles.optionLeading },
            selectionMarker,
            selectionSpacer,
            optionIcon));
    };
    const handleOptionClick = (option) => {
        if (multiple) {
            const selectedOptions = options.filter((item) => selectedValues.includes(getOptionId(item)));
            const nextOptions = isOptionSelected(option)
                ? selectedOptions.filter((item) => getOptionId(item) !== getOptionId(option))
                : [...selectedOptions, option];
            setInputValue(nextOptions.map((item) => item.text).join(', '));
            setFilterValue('');
            if (onChange) {
                onChange({
                    target: {
                        name,
                        text: nextOptions.map((item) => item.text).join(', '),
                        value: nextOptions.map((item) => item.value),
                    },
                });
            }
            setFocused(false);
            return;
        }
        setInputValue(option.text);
        if (onChange) {
            onChange({ target: { name, text: option.text, value: option.value } });
        }
        setFocused(false);
    };
    const handleOnChange = (e) => {
        if (multiple) {
            filterOptions(options, e.target.value);
            return;
        }
        if (variant === 'inputWithSearch') {
            setInputValue(e.target.value);
            setFilterValue(e.target.value);
        }
        if (variant === 'selectWithSearch') {
            filterOptions(options, e.target.value);
        }
        onChange(e);
    };
    useEffect(() => {
        if (!focused && variant === 'selectWithSearch' && options.length > 0) {
            const match = options.map((option) => option.text).includes(value);
            if (!match) {
                setInputValue('');
            }
        }
    }, [focused, options, value, variant]);
    useEffect(() => {
        if (!focused && multiple) {
            setInputValue(value || '');
            setFilterValue('');
        }
    }, [focused, multiple, value]);
    return (React.createElement("div", { className: `${styles.wrap} ${className}`, "data-test": testId },
        React.createElement(Dropdown, { controlled: true, open: focused && filteredOptions.length > 0, shape: "block", position: "bottomLeft", matchTriggerWidth: true, onClose: () => setFocused(false), testId: "autocomplete-dropdown", trigger: React.createElement(Input, { name: name, value: inputValue, size: size, onChange: (e) => handleOnChange(e), onFocus: () => setFocused(true), onBlur: () => {
                    setFocused(false);
                    onBlur === null || onBlur === void 0 ? void 0 : onBlur();
                }, disabled: disabled, error: error }) }, filteredOptions.map((option) => (React.createElement(Button, { align: "left", key: shortid(), theme: "ghost", leftIcon: getOptionLeading(option), onClick: () => handleOptionClick(option) }, option.text))))));
}
export default memo(Autocomplete);
